import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  InputAdornment,
  Divider,
  Card,
  CardContent,
  IconButton,
  Popover,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import { API_URL } from '../../config';
import { showErrorToast, showSuccessToast } from '../../toastUtils';
import { getErrorMessage } from '../../error-helpers/errorMessage';
import { PhotoCamera, ColorLens, BusinessCenter, RestartAlt } from '@mui/icons-material';
import { useAppSettings } from '../../contexts/AppSettingsContext';

// Import color picker
import { HexColorPicker } from 'react-colorful';

// Default settings values
const DEFAULT_SETTINGS = {
  companyName: 'Tranzcribe',
  primaryColor: '#5D5FEF',
  secondaryColor: '#4C4DCD',
  logoUrl: null
};

const AppCustomization: React.FC = () => {
  const { settings, refetchSettings, getLogoUrl } = useAppSettings();
  const { getToken } = useAuth();
  
  const [companyName, setCompanyName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [previewLogo, setPreviewLogo] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Display logo state
  const [displayLogo, setDisplayLogo] = useState<string | null>(null);
  
  // Color picker state
  const [primaryColorAnchorEl, setPrimaryColorAnchorEl] = useState<HTMLElement | null>(null);
  const [secondaryColorAnchorEl, setSecondaryColorAnchorEl] = useState<HTMLElement | null>(null);
  
  // Reset confirmation dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Cache the current logo URL to prevent unnecessary reloads
  const currentLogoUrl = useRef<string>(getLogoUrl());
  
  // Initialize form with current settings only once when component mounts
  // or when settings actually change their values
  useEffect(() => {
    setCompanyName(settings.companyName);
    setPrimaryColor(settings.primaryColor);
    setSecondaryColor(settings.secondaryColor);
    
    // Update the cached logo URL
    currentLogoUrl.current = getLogoUrl();
    
    // Set the display logo if no preview is active
    if (!previewLogo && !uploadSuccess) {
      setDisplayLogo(getLogoUrl());
    }
  }, [
    settings.companyName, 
    settings.primaryColor, 
    settings.secondaryColor,
    getLogoUrl,
    previewLogo,
    uploadSuccess
  ]);
  
  // Memoize the handler to avoid recreating it on each render
  const handleSelectLogo = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          const previewUrl = e.target.result as string;
          setPreviewLogo(previewUrl);
          setDisplayLogo(previewUrl);
        }
      };
      reader.readAsDataURL(file);
    }
  }, []);
  
  const handleUploadLogo = async () => {
    if (!selectedFile) {
      showErrorToast('Please select a logo to upload');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const formData = new FormData();
      formData.append('logo', selectedFile);
      
      const response = await axios.post(`${API_URL}/app-settings/logo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      // Mark upload as successful but keep showing the preview
      setUploadSuccess(true);
      showSuccessToast('Logo uploaded successfully');
      setSuccess('Logo has been updated successfully');
      
      // Keep displaying the current preview
      if (previewLogo) {
        setDisplayLogo(previewLogo);
      }
      
      // Refetch settings in the background
      refetchSettings().then(() => {
        // After a successful refetch, we'll prepare for the next upload
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
        
        // But we'll continue showing the current logo until a new selection is made
        setTimeout(() => {
          setUploadSuccess(false);
          // Only clear the preview after a delay, but keep showing the uploaded logo
          setPreviewLogo(null);
          setDisplayLogo(getLogoUrl());
        }, 3000);
      });
      
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(`Failed to upload logo: ${getErrorMessage(err)}`);
      showErrorToast(`Failed to upload logo: ${getErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await axios.put(`${API_URL}/app-settings`, {
        companyName,
        primaryColor,
        secondaryColor
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      showSuccessToast('App settings updated successfully');
      setSuccess('App settings have been updated successfully');
      
      // Refetch settings to update throughout the app
      await refetchSettings();
    } catch (err) {
      console.error('Error updating app settings:', err);
      setError(`Failed to update app settings: ${getErrorMessage(err)}`);
      showErrorToast(`Failed to update settings: ${getErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle reset to default settings
  const handleResetSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Reset all settings to default
      const response = await axios.post(`${API_URL}/app-settings/reset`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      // Update form values
      setCompanyName(DEFAULT_SETTINGS.companyName);
      setPrimaryColor(DEFAULT_SETTINGS.primaryColor);
      setSecondaryColor(DEFAULT_SETTINGS.secondaryColor);
      setPreviewLogo(null);
      setSelectedFile(null);
      setDisplayLogo(null);
      
      showSuccessToast('App settings reset successfully');
      setSuccess('App settings have been reset to default values');
      
      // Refetch settings to update throughout the app
      await refetchSettings();
      
      // Update the cached logo URL
      currentLogoUrl.current = getLogoUrl();
    } catch (err) {
      console.error('Error resetting app settings:', err);
      setError(`Failed to reset app settings: ${getErrorMessage(err)}`);
      showErrorToast(`Failed to reset settings: ${getErrorMessage(err)}`);
    } finally {
      setLoading(false);
      setResetDialogOpen(false);
    }
  };
  
  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
  };
  
  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
  };
  
  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  // Color picker handling
  const handleOpenPrimaryColorPicker = (event: React.MouseEvent<HTMLElement>) => {
    setPrimaryColorAnchorEl(event.currentTarget);
  };
  
  const handleClosePrimaryColorPicker = () => {
    setPrimaryColorAnchorEl(null);
  };
  
  const handleOpenSecondaryColorPicker = (event: React.MouseEvent<HTMLElement>) => {
    setSecondaryColorAnchorEl(event.currentTarget);
  };
  
  const handleCloseSecondaryColorPicker = () => {
    setSecondaryColorAnchorEl(null);
  };
  
  const primaryColorOpen = Boolean(primaryColorAnchorEl);
  const secondaryColorOpen = Boolean(secondaryColorAnchorEl);
  
  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        App Customization
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      <Grid container spacing={4}>
        {/* Logo Upload Section */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhotoCamera sx={{ mr: 1 }} />
                Company Logo
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Paper
                  elevation={3}
                  sx={{
                    width: 250,
                    height: 120,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    overflow: 'hidden'
                  }}
                >
                  {displayLogo ? (
                    <img 
                      src={displayLogo} 
                      alt="Logo" 
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                    />
                  ) : (
                    <Typography color="text.secondary">No logo uploaded</Typography>
                  )}
                </Paper>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  sx={{ textTransform: 'none' }}
                >
                  Select Logo
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleSelectLogo}
                  />
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handleUploadLogo}
                  disabled={loading || !selectedFile}
                  sx={{ textTransform: 'none' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Upload Logo'}
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Recommended size: 250x80 pixels. Max file size: 5MB.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Text and Color Settings */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessCenter sx={{ mr: 1 }} />
                Company Information
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Company Name"
                  fullWidth
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessCenter />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label="Primary Color"
                    fullWidth
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    variant="outlined"
                    placeholder="#5D5FEF"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              backgroundColor: primaryColor || '#5D5FEF',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }} 
                            onClick={handleOpenPrimaryColorPicker}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleOpenPrimaryColorPicker}>
                            <ColorLens />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Popover
                    open={primaryColorOpen}
                    anchorEl={primaryColorAnchorEl}
                    onClose={handleClosePrimaryColorPicker}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <HexColorPicker 
                        color={primaryColor || '#5D5FEF'} 
                        onChange={setPrimaryColor} 
                      />
                    </Box>
                  </Popover>
                  
                  <TextField
                    label="Secondary Color"
                    fullWidth
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    variant="outlined"
                    placeholder="#4C4DCD"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              backgroundColor: secondaryColor || '#4C4DCD',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }} 
                            onClick={handleOpenSecondaryColorPicker}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleOpenSecondaryColorPicker}>
                            <ColorLens />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Popover
                    open={secondaryColorOpen}
                    anchorEl={secondaryColorAnchorEl}
                    onClose={handleCloseSecondaryColorPicker}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <HexColorPicker 
                        color={secondaryColor || '#4C4DCD'} 
                        onChange={setSecondaryColor} 
                      />
                    </Box>
                  </Popover>
                </Box>
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleOpenResetDialog}
                  disabled={loading}
                  startIcon={<RestartAlt />}
                  sx={{ textTransform: 'none' }}
                >
                  Reset to Default
                </Button>
                
                <Button
                  variant="contained"
                  onClick={handleSaveSettings}
                  disabled={loading}
                  sx={{ textTransform: 'none' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Settings'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Reset confirmation dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCloseResetDialog}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          Reset Customization
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            Are you sure you want to reset all customization settings to their default values? 
            This will remove your custom company name, colors, and logo. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleResetSettings} color="error" autoFocus>
            Reset All Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppCustomization; 